import React from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { HsnCode, HsnCodeCreate, HsnCodeUpdate } from '@/app/types/hsnCode';

// Zod Schema for HSN Code Form
const formSchema = z.object({
  code: z.string().min(1, 'HSN code is required').max(10)
    .refine(s => /^\d+$/.test(s), 'HSN code must contain only digits'),
  description: z.string().min(1, 'Description is required').max(255),
  tax_rate: z.number().optional().nullable(),
  is_active: z.boolean(),
});

type HsnCodeFormValues = z.infer<typeof formSchema>;

interface HsnCodeFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: HsnCodeCreate | HsnCodeUpdate) => void;
  defaultValues?: Partial<HsnCode>;
  isLoading?: boolean;
  mode: 'create' | 'edit';
}

const HsnCodeForm: React.FC<HsnCodeFormProps> = ({
  open,
  onOpenChange,
  onSubmit,
  defaultValues,
  isLoading = false,
  mode,
}) => {
  const form = useForm<HsnCodeFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      code: defaultValues?.code || '',
      description: defaultValues?.description || '',
      tax_rate: defaultValues?.tax_rate ?? null,
      is_active: defaultValues?.is_active ?? true,
    },
  });

  // Disable code editing in edit mode
  const isCodeDisabled = mode === 'edit';

  const handleSubmit: SubmitHandler<HsnCodeFormValues> = (values) => {
    const dataToSend = mode === 'edit'
      ? { 
          description: values.description, 
          tax_rate: values.tax_rate,
          is_active: values.is_active 
        }
      : values;
    onSubmit(dataToSend);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Add New HSN Code' : 'Edit HSN Code'}</DialogTitle>
          <DialogDescription>
            {mode === 'create' ? 'Define a new HSN code.' : 'Update the HSN code details.'}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>HSN Code*</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="e.g., 8471" disabled={isCodeDisabled} />
                  </FormControl>
                  {isCodeDisabled && (
                    <FormDescription>HSN Code cannot be changed after creation.</FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description*</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Enter description"
                      value={field.value || ''}
                      rows={3}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="tax_rate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Default Tax Rate (%)</FormLabel>
                  <FormControl>
                    <Input 
                      type="number"
                      step="0.01"
                      {...field}
                      placeholder="e.g., 18"
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
                    />
                  </FormControl>
                  <FormDescription>Optional: Set a default tax rate associated with this HSN.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox 
                      checked={field.value} 
                      onCheckedChange={field.onChange} 
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="font-normal">Active</FormLabel>
                    <FormDescription>
                      Inactive HSN codes won&apos;t be available for selection.
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {isLoading 
                  ? (mode === 'create' ? 'Creating...' : 'Updating...') 
                  : (mode === 'create' ? 'Create HSN Code' : 'Save Changes')
                }
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default HsnCodeForm; 